import React, { useState } from 'react';
import { RedChecker, BlackChecker, RedKing, BlackKing } from './Icons.js';

/**
 * Checkers Game Component
 * 
 * Game Rules:
 * - 8x8 checkered board where only dark squares are used for gameplay
 * - Each player starts with 12 pieces on the three nearest rows of dark squares
 * - Pieces move diagonally forward one square
 * - Pieces capture by jumping over opponent's piece to empty space beyond
 * - Multiple captures in one move are allowed and mandatory if available
 * - Pieces become "kings" when reaching opponent's back row
 * - Kings can move diagonally both forward and backward
 * - Game ends when one player has no pieces or no valid moves
 */
const Checkers = () => {
  // Initialize board with pieces
  const initializeBoard = () => {
    const board = Array(64).fill(null);
    
    // Dark squares on an 8x8 checkerboard (standard positions)
    // Rows 0-2: Black pieces (player 2)
    // Rows 5-7: Red pieces (player 1)
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const index = row * 8 + col;
        const isDarkSquare = (row + col) % 2 === 1;
        
        if (isDarkSquare) {
          if (row < 3) {
            board[index] = { player: 2, isKing: false }; // Black pieces
          } else if (row > 4) {
            board[index] = { player: 1, isKing: false }; // Red pieces
          }
        }
      }
    }
    
    return board;
  };

  const [board, setBoard] = useState(initializeBoard());
  const [currentPlayer, setCurrentPlayer] = useState(1); // Player 1 (red) starts
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [winner, setWinner] = useState(null);
  const [mustContinueCapture, setMustContinueCapture] = useState(null);

  /**
   * Get row and column from board index
   */
  const getRowCol = (index) => {
    return {
      row: Math.floor(index / 8),
      col: index % 8
    };
  };

  /**
   * Get board index from row and column
   */
  const getIndex = (row, col) => {
    if (row < 0 || row > 7 || col < 0 || col > 7) return null;
    return row * 8 + col;
  };

  /**
   * Check if a square is a dark square (playable)
   */
  const isDarkSquare = (row, col) => {
    return (row + col) % 2 === 1;
  };

  /**
   * Get all possible moves for a piece at the given index
   * Returns object with simple moves and captures
   */
  const getPossibleMoves = (index, currentBoard = board) => {
    const piece = currentBoard[index];
    if (!piece) return { simple: [], captures: [] };

    const { row, col } = getRowCol(index);
    const simple = [];
    const captures = [];

    // Determine movement directions based on piece type
    const directions = piece.isKing 
      ? [[-1, -1], [-1, 1], [1, -1], [1, 1]] // Kings move in all diagonal directions
      : piece.player === 1 
        ? [[-1, -1], [-1, 1]] // Red moves up (negative row direction)
        : [[1, -1], [1, 1]];  // Black moves down (positive row direction)

    for (const [dRow, dCol] of directions) {
      const newRow = row + dRow;
      const newCol = col + dCol;
      const newIndex = getIndex(newRow, newCol);

      if (newIndex !== null && isDarkSquare(newRow, newCol)) {
        const targetSquare = currentBoard[newIndex];

        // Simple move (empty square)
        if (!targetSquare) {
          simple.push(newIndex);
        }
        // Capture move (opponent piece with empty square beyond)
        else if (targetSquare.player !== piece.player) {
          const jumpRow = newRow + dRow;
          const jumpCol = newCol + dCol;
          const jumpIndex = getIndex(jumpRow, jumpCol);

          if (jumpIndex !== null && isDarkSquare(jumpRow, jumpCol) && !currentBoard[jumpIndex]) {
            captures.push({
              to: jumpIndex,
              captured: newIndex
            });
          }
        }
      }
    }

    return { simple, captures };
  };

  /**
   * Check if any piece of the current player has a capture available
   */
  const hasAvailableCaptures = (player, currentBoard = board) => {
    for (let i = 0; i < 64; i++) {
      const piece = currentBoard[i];
      if (piece && piece.player === player) {
        const { captures } = getPossibleMoves(i, currentBoard);
        if (captures.length > 0) {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * Get all valid moves for a selected piece
   * If captures are available anywhere for the player, only captures are valid
   */
  const getValidMovesForPiece = (index) => {
    const moves = getPossibleMoves(index);
    
    // If this is a continuation of a multi-capture, only show capture moves from this piece
    if (mustContinueCapture !== null && mustContinueCapture === index) {
      return moves.captures.map(c => c.to);
    }
    
    // If any piece can capture, only captures are valid
    if (hasAvailableCaptures(currentPlayer)) {
      return moves.captures.map(c => c.to);
    }
    
    // Otherwise, return all simple moves
    return moves.simple;
  };

  /**
   * Check if a move results in a king promotion
   */
  const shouldPromoteToKing = (index, player) => {
    const { row } = getRowCol(index);
    return (player === 1 && row === 0) || (player === 2 && row === 7);
  };

  /**
   * Handle clicking on a board square
   */
  const handleSquareClick = (index) => {
    if (winner) return;

    const piece = board[index];
    const { row, col } = getRowCol(index);
    
    // Can only click on dark squares
    if (!isDarkSquare(row, col)) return;

    // If continuing a multi-capture, can only click on valid move squares
    if (mustContinueCapture !== null) {
      if (validMoves.includes(index)) {
        performMove(mustContinueCapture, index);
      }
      return;
    }

    // Selecting own piece
    if (piece && piece.player === currentPlayer) {
      setSelectedPiece(index);
      const moves = getValidMovesForPiece(index);
      setValidMoves(moves);
    }
    // Moving to a valid destination
    else if (selectedPiece !== null && validMoves.includes(index)) {
      performMove(selectedPiece, index);
    }
    // Clicking elsewhere deselects
    else {
      setSelectedPiece(null);
      setValidMoves([]);
    }
  };

  /**
   * Perform a move from one square to another
   */
  const performMove = (fromIndex, toIndex) => {
    const newBoard = [...board];
    const piece = newBoard[fromIndex];
    const { row: fromRow, col: fromCol } = getRowCol(fromIndex);
    const { row: toRow, col: toCol } = getRowCol(toIndex);

    // Check if this is a capture move
    const rowDiff = Math.abs(toRow - fromRow);
    const isCapture = rowDiff === 2;

    if (isCapture) {
      // Remove captured piece
      const capturedRow = (fromRow + toRow) / 2;
      const capturedCol = (fromCol + toCol) / 2;
      const capturedIndex = getIndex(capturedRow, capturedCol);
      newBoard[capturedIndex] = null;
    }

    // Move the piece
    newBoard[toIndex] = { ...piece };
    newBoard[fromIndex] = null;

    // Check for king promotion
    if (shouldPromoteToKing(toIndex, piece.player)) {
      newBoard[toIndex].isKing = true;
    }

    setBoard(newBoard);

    // Check for multi-capture
    if (isCapture) {
      const { captures } = getPossibleMoves(toIndex, newBoard);
      if (captures.length > 0) {
        // Must continue capturing
        setMustContinueCapture(toIndex);
        setSelectedPiece(toIndex);
        setValidMoves(captures.map(c => c.to));
        return;
      }
    }

    // Move complete, switch players
    setSelectedPiece(null);
    setValidMoves([]);
    setMustContinueCapture(null);

    const nextPlayer = currentPlayer === 1 ? 2 : 1;
    
    // Check if next player has any valid moves
    if (!hasAnyValidMoves(nextPlayer, newBoard)) {
      setWinner(currentPlayer);
    } else {
      setCurrentPlayer(nextPlayer);
    }
  };

  /**
   * Check if a player has any valid moves
   */
  const hasAnyValidMoves = (player, currentBoard = board) => {
    let hasCaptures = false;
    let hasSimpleMoves = false;
    
    // Single loop to check both captures and simple moves
    for (let i = 0; i < 64; i++) {
      const piece = currentBoard[i];
      if (piece && piece.player === player) {
        const { simple, captures } = getPossibleMoves(i, currentBoard);
        
        if (captures.length > 0) {
          hasCaptures = true;
          return true; // If captures exist, they're always valid moves
        }
        if (simple.length > 0) {
          hasSimpleMoves = true;
        }
      }
    }
    
    // If no captures found, simple moves are valid
    return hasSimpleMoves;
  };

  /**
   * Reset the game
   */
  const resetGame = () => {
    setBoard(initializeBoard());
    setCurrentPlayer(1);
    setSelectedPiece(null);
    setValidMoves([]);
    setWinner(null);
    setMustContinueCapture(null);
  };

  /**
   * Render the content of a square
   */
  const renderSquare = (index) => {
    const piece = board[index];
    if (!piece) return null;

    if (piece.player === 1) {
      return piece.isKing ? <RedKing /> : <RedChecker />;
    } else {
      return piece.isKing ? <BlackKing /> : <BlackChecker />;
    }
  };

  /**
   * Get the status message
   */
  const getStatus = () => {
    if (winner) {
      const winnerName = winner === 1 ? 'Vermelhas' : 'Pretas';
      return `Vencedor: ${winnerName}!`;
    } else if (mustContinueCapture !== null) {
      return 'Continua a capturar com a peça selecionada!';
    } else {
      const playerName = currentPlayer === 1 ? 'Vermelhas' : 'Pretas';
      return `Turno: ${playerName}`;
    }
  };

  /**
   * Get CSS class for a square
   */
  const getSquareClass = (index) => {
    const { row, col } = getRowCol(index);
    const isLight = (row + col) % 2 === 0;
    const isSelected = selectedPiece === index;
    const isValidMove = validMoves.includes(index);
    
    let className = 'checkers-square';
    if (isLight) className += ' light-square';
    else className += ' dark-square';
    if (isSelected) className += ' selected';
    if (isValidMove) className += ' valid-move';
    
    return className;
  };

  return (
    <div className="game-container">
      <h1 className="game-title">Damas</h1>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      <div className="checkers-board">
        {board.map((_, index) => (
          <button
            key={index}
            className={getSquareClass(index)}
            onClick={() => handleSquareClick(index)}
            disabled={winner !== null}
          >
            {renderSquare(index)}
          </button>
        ))}
      </div>
      <button className="new-game-button" onClick={resetGame}>
        Novo Jogo
      </button>
    </div>
  );
};

export default Checkers;
